import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import type {
  ColumnConfig,
  ColumnGroup,
  OperationComponentProps,
  PaginatedResponse,
} from '@/components/dataTable/types'
import type { DemoUser as User } from '@/components/playground/DataTableWidget'
import { DemoPageHero, DemoStage } from '@/components/editorial/DemoPageLayout'
import SectionHeader from '@/components/editorial/SectionHeader'
import ApplicationEnquiry from '@/components/editorial/ApplicationEnquiry'
import DeferredDataTableUsage from '@/components/playground/DeferredDataTableUsage'
import Reveal from '@/components/preview/Reveal'
import { createDataTableRows } from '@/lib/datatableDemoData'

const DataTableWidget = lazy(
  () => import('@/components/playground/DataTableWidget'),
)

export const Route = createFileRoute('/playground/datatable')({
  component: DataTablePage,
  head: () => {
    const url = 'https://www.suriyaprakash.in/playground/datatable'
    const title = 'React DataTable Demo | Suriyaprakash Somu'
    const description =
      'Try a reusable React DataTable for business records: sorting, pagination, row selection and configurable columns. Explore the TanStack Table implementation.'

    return {
      meta: [
        { title },
        { name: 'title', content: title },
        { name: 'description', content: description },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        { name: 'geo.placename', content: 'Erode, Tamil Nadu, India' },
        {
          name: 'keywords',
          content:
            'React DataTable, TanStack Table demo, sortable table, paginated table, row selection UI, column drag and drop, column visibility picker, TypeScript DataTable component, Suriyaprakash Somu',
        },
        {
          name: 'ai-summary',
          content:
            'This page demonstrates a reusable React DataTable with sorting, pagination, row selection details, column visibility controls, and drag-and-drop column reordering over 1,000 employee rows.',
        },
        { property: 'og:title', content: title },
        { property: 'og:image:alt', content: title },
        { name: 'twitter:image:alt', content: title },
        { property: 'og:description', content: description },
        { property: 'og:locale', content: 'en_IN' },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:image',
          content: 'https://www.suriyaprakash.in/og/playground-datatable.png',
        },
        { property: 'og:url', content: url },
        { property: 'og:site_name', content: 'Suriyaprakash Somu' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        {
          name: 'twitter:image',
          content: 'https://www.suriyaprakash.in/og/playground-datatable.png',
        },
      ],
      links: [
        { rel: 'canonical', href: url },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: '/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'React DataTable Playground',
            applicationCategory: 'DeveloperApplication',
            operatingSystem: 'Web',
            url,
            description,
            creator: {
              '@type': 'Person',
              '@id': 'https://www.suriyaprakash.in/#person',
              url: 'https://www.suriyaprakash.in/',
              name: 'Suriyaprakash Somu',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Erode',
                addressRegion: 'Tamil Nadu',
                addressCountry: 'IN',
              },
            },
            featureList: [
              'TanStack Table pagination',
              'Sortable columns',
              'Row selection and selected-row summary',
              'Column visibility picker',
              'Drag-and-drop column reordering',
              'Deterministic employee dataset',
            ],
            keywords: ['TypeScript', 'React', 'TanStack Table'],
          }),
        },
      ],
    }
  },
})

function ViewUserDialog({ data, onCancel }: OperationComponentProps<User>) {
  if (!data) return null

  const initials = data.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const statusConfig: Record<string, { color: string; label: string }> = {
    Active: { color: 'bg-emerald-500', label: 'Active' },
    Inactive: { color: 'bg-rose-400', label: 'Inactive' },
    Pending: { color: 'bg-amber-400', label: 'Pending' },
  }

  const status = statusConfig[data.status] ?? {
    color: 'bg-stone-400',
    label: data.status,
  }

  return (
    <div className="-mx-6 -mb-6 -mt-6 flex h-full flex-col">
      <div className="relative flex items-end gap-5 bg-gradient-to-br from-forest to-forest-strong px-6 pb-5 pt-12">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 text-2xl font-bold tracking-wide text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] backdrop-blur">
          {initials}
        </div>
        <div className="flex-1 pb-0.5">
          <h2 className="text-xl font-bold text-white">{data.name}</h2>
          <p className="text-sm text-white/75">{data.email}</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-black/15 text-white/70 transition-colors hover:bg-black/30 hover:text-white"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
        <div className="flex flex-wrap gap-1.5">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-white ${status.color}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white/60" />
            {status.label}
          </span>
          <span className="inline-flex items-center rounded-full border border-line/70 bg-cream-50 px-3 py-1 text-xs font-semibold text-ink-soft">
            ID #{data.id}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-line/60 bg-card/60 p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
              Role
            </p>
            <p className="mt-1 font-semibold text-ink">{data.role}</p>
          </div>
          <div className="rounded-xl border border-line/60 bg-card/60 p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
              Department
            </p>
            <p className="mt-1 font-semibold text-ink">{data.department}</p>
          </div>
          <div className="rounded-xl border border-line/60 bg-card/60 p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
              Joined
            </p>
            <p className="mt-1 font-semibold text-ink">{data.joinedAt}</p>
          </div>
          <div className="rounded-xl border border-line/60 bg-card/60 p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
              Salary
            </p>
            <p className="mt-1 font-mono tabular-nums font-semibold text-ink">
              ${data.salary.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-sage-200/70 bg-sage-50/50 p-4">
          <div className="flex items-center gap-2 text-xs text-ink-soft">
            <span className="inline-flex items-center gap-1 rounded-md bg-forest/10 px-2 py-0.5 font-semibold text-forest">
              {data.department.substring(0, 4).toUpperCase()}
            </span>
            <span>Employee since {data.joinedAt}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function DataTablePage() {
  const demoRef = useRef<HTMLDivElement>(null)
  const [showDemo, setShowDemo] = useState(false)
  const userData = useMemo(
    () => createDataTableRows(1000, 42) satisfies Array<User>,
    [],
  )

  useEffect(() => {
    const element = demoRef.current
    if (!element) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.2) {
          setShowDemo(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  const fetchData = useCallback(
    (params: {
      page?: number
      pageSize?: number
      sorting?: string
    }): Promise<PaginatedResponse<User>> => {
      const page = params.page || 1
      const pageSize = params.pageSize || 25

      let sorted = userData
      if (params.sorting) {
        const rules: Array<{ id: string; desc: boolean }> = JSON.parse(
          params.sorting,
        )
        sorted = [...userData].sort((a, b) => {
          for (const rule of rules) {
            const aVal = a[rule.id as keyof typeof a]
            const bVal = b[rule.id as keyof typeof b]
            if (aVal == null && bVal == null) continue
            if (aVal == null) return 1
            if (bVal == null) return -1
            let cmp: number
            if (typeof aVal === 'string' && typeof bVal === 'string') {
              cmp = aVal.localeCompare(bVal)
            } else {
              cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
            }
            if (cmp !== 0) return rule.desc ? -cmp : cmp
          }
          return 0
        })
      }

      const start = (page - 1) * pageSize
      const end = start + pageSize
      const rows = sorted.slice(start, end)
      return Promise.resolve({ rows, rowCount: userData.length })
    },
    [userData],
  )

  const columnsConfig = useMemo<Array<ColumnGroup<User>>>(() => {
    const cols: Array<ColumnConfig<User>> = [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        enableSorting: true,
      },
      {
        id: 'role',
        accessorKey: 'role',
        header: 'Role',
        enableSorting: true,
        cellClassName: 'text-ink-soft',
      },
      {
        id: 'department',
        accessorKey: 'department',
        header: 'Department',
        enableSorting: true,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ getValue }) => {
          const status = getValue() as string
          const colors: Record<string, string> = {
            Active:
              'border-emerald-700/20 bg-emerald-50 text-emerald-800 shadow-[inset_0_0_0_1px_rgba(4,120,87,0.12)]',
            Inactive:
              'border-rose-700/20 bg-rose-50 text-rose-800 shadow-[inset_0_0_0_1px_rgba(190,18,60,0.12)]',
            Pending:
              'border-amber-700/25 bg-amber-50 text-amber-900 shadow-[inset_0_0_0_1px_rgba(180,83,9,0.14)]',
          }
          const color =
            colors[status] ?? 'border-line bg-cream-100 text-ink-soft'
          return (
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold ${color}`}
            >
              {status}
            </span>
          )
        },
      },
      {
        id: 'joinedAt',
        accessorKey: 'joinedAt',
        header: 'Joined',
        enableSorting: true,
      },
      {
        id: 'salary',
        accessorKey: 'salary',
        header: 'Salary',
        enableSorting: true,
        cell: ({ getValue }) => {
          const val = getValue() as number
          return (
            <span className="font-mono tabular-nums">
              ${val.toLocaleString()}
            </span>
          )
        },
      },
    ]

    return [{ id: 'main', columns: cols }]
  }, [])

  return (
    <>
      <DemoPageHero
        system="SYS.01"
        eyebrow="React DataTable · TanStack Table"
        title="React DataTable."
        accent="Business records, organized."
        description="A reusable table for directories, inventory and administration screens. Try sorting, pagination, row selection and configurable columns with 1,000 generated employee records; this demo runs locally in your browser."
        specs={[
          { label: 'Records', value: '1,000' },
          { label: 'Columns', value: '7' },
          { label: 'Actions', value: 'Sort + select' },
          { label: 'Layout', value: 'Drag headers' },
        ]}
      />

      <DemoStage
        system="SYS.01"
        hint="Click a header to sort, drag a header to reorder columns, and tick rows to select them. Open a row's menu to view its details."
      >
        <div ref={demoRef} className="min-h-[32rem]">
          {showDemo ? (
            <Suspense fallback={<DataTablePlaceholder />}>
              <DataTableWidget
                tag="employees"
                title="Employee Directory"
                subject="employees"
                moduleKey="employees"
                fetchData={fetchData}
                columnsConfig={columnsConfig}
                operations={{
                  view: {
                    component: ViewUserDialog,
                    displayMode: 'sheet',
                    width: 'md',
                  },
                }}
                enableSelection
                showPagination
                showDataHeader
                density="base"
                tone="default"
              />
            </Suspense>
          ) : (
            <DataTablePlaceholder />
          )}
        </div>
      </DemoStage>

      <section className="bg-cream-50 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Anatomy"
            title="One grid,"
            accent="two layers."
            description="The component separates a dataset-agnostic table contract from the interactions people need to work through dense operational data."
          />

          <div className="mt-16 grid gap-14 border-t border-ink/15 pt-10 lg:grid-cols-2 lg:gap-20 sm:mt-20 sm:pt-14">
            <Reveal>
              <article className="grid gap-5 sm:grid-cols-[3rem_1fr]">
                <span className="preview-mono text-xs font-bold text-forest">
                  01
                </span>
                <div>
                  <h3 className="font-heading text-3xl tracking-[-0.025em] sm:text-4xl">
                    What it is
                  </h3>
                  <div className="mt-6 space-y-5 text-base leading-8 text-ink-soft">
                    <p>
                      A production-style React DataTable built with{' '}
                      <strong className="font-semibold text-ink">
                        TanStack Table
                      </strong>
                      . It renders a pageable, sortable grid over 1,000
                      deterministic employee records, paginated locally for fast
                      interaction, but the component itself is dataset-agnostic.
                    </p>
                    <p>
                      Pass it a fetch function, a column configuration, and
                      optional row operations, and it handles the rest. This is
                      the kind of table used in admin panels, ERP modules, and
                      internal business applications: dense, fast,
                      keyboard-friendly, and designed for real batch-selection
                      workflows.
                    </p>
                    <p>
                      The demo uses the reusable DataTable component with a
                      local fetch function. A business application would connect
                      that contract to its API and enforce access rules on the
                      server.
                    </p>
                  </div>
                </div>
              </article>
            </Reveal>

            <Reveal delay={100}>
              <article className="grid gap-5 sm:grid-cols-[3rem_1fr]">
                <span className="preview-mono text-xs font-bold text-forest">
                  02
                </span>
                <div>
                  <h3 className="font-heading text-3xl tracking-[-0.025em] sm:text-4xl">
                    How it works
                  </h3>
                  <div className="mt-6 space-y-5 text-base leading-8 text-ink-soft">
                    <p>
                      A typed{' '}
                      <strong className="font-semibold text-ink">
                        column configuration
                      </strong>{' '}
                      declares each accessor, header, sorting behaviour, and
                      optional custom cell renderer. Column groups can bundle
                      fields under a shared header. TanStack Table owns
                      pagination, sorting, and row-selection state while the
                      fetch function supplies the active page.
                    </p>
                    <p>
                      Drag each header by its grip to reorder columns, click a
                      header to sort, or hide fields from the Columns menu. The
                      selection summary updates as rows are checked, and a row
                      "view" operation opens a detail sheet through the same
                      operation pipeline exposed to custom actions.
                    </p>
                  </div>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 text-cream-50 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            tone="dark"
            eyebrow="Integration"
            title="Typed in,"
            accent="operable out."
            description="Define the data contract, connect the page-aware fetch function, and opt into the operations the screen needs."
          />
          <Reveal delay={100} className="mt-12 sm:mt-16">
            <DeferredDataTableUsage
              code={`import { DataTable } from '@/components/dataTable/DataTable'
import type { ColumnGroup } from '@/components/dataTable/types'

type User = { id: number; name: string; email: string; status: string; salary: number }

export default function EmployeeTable() {
  const columnsConfig: ColumnGroup<User>[] = [{
    id: 'main',
    columns: [
      { id: 'name', accessorKey: 'name', header: 'Name', enableSorting: true },
      { id: 'email', accessorKey: 'email', header: 'Email', enableSorting: true },
      { id: 'status', accessorKey: 'status', header: 'Status',
        cell: ({ getValue }) => <Badge>{String(getValue())}</Badge> },
      { id: 'salary', accessorKey: 'salary', header: 'Salary',
        cell: ({ getValue }) => \`$\${(getValue() as number).toLocaleString()}\` },
    ]
  }]

  const fetchData = async (params) => {
    // params: { page, pageSize, sorting, filters }
    const rows = await getUsers(params)
    return { rows, rowCount: rows.length }
  }

  return (
    <DataTable
      tag="employees"
      title="Employee Directory"
      fetchData={fetchData}
      columnsConfig={columnsConfig}
      operations={{ view: { component: ViewUser, displayMode: 'sheet', width: 'md' } }}
      enableSelection
      showPagination
    />
  )
}`}
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-cream-50 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Fit"
            title="Repeatable where"
            accent="density matters."
            description="A focused feature set for product surfaces that reuse the same operational grid pattern across many screens."
          />

          <div className="mt-16 grid gap-16 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)] lg:gap-24 sm:mt-20">
            <Reveal>
              <div>
                <p className="preview-mono mb-5 text-[10px] font-bold uppercase tracking-[0.22em] text-forest">
                  Features / 06
                </p>
                <ul className="border-b border-ink/15">
                  {[
                    'Paginated 1,000-row dataset',
                    'Sortable columns across all data types',
                    'Row selection with selected-row summary',
                    'Column visibility picker',
                    'Drag-and-drop column reordering',
                    'Typed column groups and custom cell renderers',
                  ].map((feature, index) => (
                    <li
                      key={feature}
                      className="grid grid-cols-[2.5rem_1fr] gap-4 border-t border-ink/15 py-5 sm:grid-cols-[3.5rem_1fr] sm:py-6"
                    >
                      <span className="preview-mono text-[10px] font-bold text-muted">
                        0{index + 1}
                      </span>
                      <span className="font-heading text-xl leading-tight sm:text-2xl">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="lg:pt-9">
                <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.22em] text-forest">
                  When to use
                </p>
                <div className="mt-6 space-y-5 text-base leading-8 text-ink-soft">
                  <p>
                    A configurable DataTable pays off when an app repeats the
                    same grid across employee directories, inventory, invoices,
                    and audit logs. Centralising sorting, pagination, and
                    selection keeps those screens consistent and substantially
                    reduces per-screen code.
                  </p>
                  <p>
                    Built with React, TypeScript, and TanStack Table. The
                    surrounding system continues in{' '}
                    <Link
                      to="/playground/schemaform"
                      className="font-semibold text-forest underline decoration-forest/30 underline-offset-4 transition-colors hover:decoration-forest"
                    >
                      the SchemaForm builder playground
                    </Link>
                    , where schema-driven forms generate the entry screens that
                    feed grids like this one, and{' '}
                    <Link
                      to="/playground/querybuilder"
                      className="font-semibold text-forest underline decoration-forest/30 underline-offset-4 transition-colors hover:decoration-forest"
                    >
                      the QueryBuilder playground
                    </Link>
                    , which demonstrates filter composition for applications
                    using this pattern. This table demo exposes sorting and
                    pagination, not a connected filtering workflow.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <ApplicationEnquiry />
    </>
  )
}

function DataTablePlaceholder() {
  return (
    <div className="grid min-h-[32rem] place-items-center rounded-[1.25rem] border border-ink/15 bg-cream-50">
      <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        Loading interactive table…
      </p>
    </div>
  )
}
